import React, {useState} from "react";
import {toast} from "react-toastify";
import Modal from "../../../components/Modal";
import InputText from "../../../components/Inputs/InputText";
import Button from "../../../components/Button";
import Loader from "../../../components/Loader";
import SelectFieldComponent, {SelectOptionInterface} from "../../../components/SelectFieldComponent";
import AdminTable, {AdminTableColumn} from "../components/AdminTable";
import ConfirmDialog from "../components/ConfirmDialog";
import {AdminPhotoOrder, AdminPhotoFile} from "../../../interfaces/admin";
import {
    useGetAdminPhotoOrdersQuery,
    useGetAdminPhotoOrderQuery,
    useRetryAdminPhotoFileMutation,
    useDeleteAdminPhotoOrderMutation,
} from "../../../storage/adminApi";
import localStyles from "../styles.module.scss";

const AdminPhotofixation = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [filterExtId, setFilterExtId] = useState("");
    const [filterBb, setFilterBb] = useState("");
    const [filter1c, setFilter1c] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [detailId, setDetailId] = useState<number | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminPhotoOrder | null>(null);

    const params: Record<string, any> = {page, per_page: pageSize, sort_by: sortBy, sort_order: sortOrder};
    if (filterExtId) params.ext_id = filterExtId;
    if (filterBb) params.upload_to_bb = filterBb;
    if (filter1c) params.sent_to_1c = filter1c;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;

    const {data, isLoading} = useGetAdminPhotoOrdersQuery(params);
    const {data: detailData, isLoading: detailLoading} = useGetAdminPhotoOrderQuery(detailId!, {skip: !detailId});
    const [retryPhotoFile] = useRetryAdminPhotoFileMutation();
    const [deletePhotoOrder] = useDeleteAdminPhotoOrderMutation();

    const handleRetry = async (fileId: number) => {
        try {
            await retryPhotoFile(fileId).unwrap();
            toast.success("File retry queued");
        } catch (e) {
            toast.error("Failed to retry file");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deletePhotoOrder(deleteTarget.id).unwrap();
            toast.success("Order deleted");
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Failed to delete order");
        }
    };

    const bbOptions: SelectOptionInterface[] = [
        {value: "", label: "All"},
        {value: "true", label: "Yes"},
        {value: "false", label: "No"},
    ];

    const sentTo1cOptions: SelectOptionInterface[] = [
        {value: "", label: "All"},
        {value: "true", label: "Yes"},
        {value: "false", label: "No"},
    ];

    const selectedBbOption = bbOptions.find((o) => o.value === filterBb) ?? null;
    const selected1cOption = sentTo1cOptions.find((o) => o.value === filter1c) ?? null;

    const columns: AdminTableColumn[] = [
        {label: "ID", key: "id", sortable: true},
        {label: "Ext ID", key: "ext_id"},
        {label: "Event", key: "event"},
        {label: "Data Type", key: "data_type"},
        {
            label: "Files",
            key: "files",
            render: (_: any, row: AdminPhotoOrder) => row.files?.length ?? 0,
        },
        {
            label: "BB",
            key: "upload_to_bb",
            render: (value: boolean) =>
                value
                    ? <span className={localStyles.statusIcon}>&#10003;</span>
                    : <span className={localStyles.statusIcon}>&#10007;</span>,
        },
        {
            label: "1C",
            key: "sent_to_1C",
            render: (value: boolean) =>
                value
                    ? <span className={localStyles.statusIcon}>&#10003;</span>
                    : <span className={localStyles.statusIcon}>&#10007;</span>,
        },
        {label: "Created", key: "created_at", sortable: true},
        {
            label: "Actions",
            key: "actions",
            render: (_: any, row: AdminPhotoOrder) => (
                <div className={localStyles.actionsCell}>
                    <Button
                        id={`view-photo-${row.id}`}
                        size="small"
                        background="bg-outline"
                        text="View"
                        click={(e) => {
                            e.stopPropagation();
                            setDetailId(row.id);
                        }}
                    />
                    <Button
                        id={`delete-photo-${row.id}`}
                        size="small"
                        background="bg-danger"
                        text="Delete"
                        click={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(row);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <h4 className="fw-600" style={{marginBottom: 16}}>Photofixation Orders</h4>

            {/* Filter Bar */}
            <div className={localStyles.filterBar}>
                <div className={localStyles.filterGroup}>
                    <label>Ext ID</label>
                    <InputText
                        id="filter-ext-id"
                        placeholder="Search..."
                        value={filterExtId}
                        handleChange={(value: string) => {
                            setFilterExtId(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>BB Upload</label>
                    <SelectFieldComponent
                        id="filter-bb"
                        options={bbOptions}
                        selectedValue={selectedBbOption}
                        placeholder="All"
                        handleSelect={(option: SelectOptionInterface) => {
                            setFilterBb(option?.value ?? "");
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>Sent to 1C</label>
                    <SelectFieldComponent
                        id="filter-1c"
                        options={sentTo1cOptions}
                        selectedValue={selected1cOption}
                        placeholder="All"
                        handleSelect={(option: SelectOptionInterface) => {
                            setFilter1c(option?.value ?? "");
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>From</label>
                    <InputText
                        id="filter-date-from"
                        type="date"
                        value={filterDateFrom}
                        handleChange={(value: string) => {
                            setFilterDateFrom(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>To</label>
                    <InputText
                        id="filter-date-to"
                        type="date"
                        value={filterDateTo}
                        handleChange={(value: string) => {
                            setFilterDateTo(value);
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <AdminTable
                columns={columns}
                data={data?.items ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                loading={isLoading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    setPage(1);
                }}
            />

            {/* Detail Modal */}
            {detailId && (
                <Modal
                    show
                    handleClose={() => setDetailId(null)}
                    width={750}
                    closeOut
                    element={
                        <div>
                            <div className={localStyles.modalTitle}>Order #{detailId} Files</div>
                            {detailLoading ? (
                                <div style={{padding: "20px 0", display: "flex", justifyContent: "center"}}>
                                    <Loader/>
                                </div>
                            ) : detailData?.files && detailData.files.length > 0 ? (
                                <div style={{overflowX: "auto"}}>
                                    <table className={localStyles.detailTable}>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>MIME</th>
                                                <th>Track</th>
                                                <th>BB</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailData.files.map((file: AdminPhotoFile) => (
                                                <tr key={file.id}>
                                                    <td>{file.id}</td>
                                                    <td>
                                                        <span className={localStyles.textEllipsis}>
                                                            {file.origin_name}
                                                        </span>
                                                    </td>
                                                    <td>{file.mime_type}</td>
                                                    <td>{file.track_number}</td>
                                                    <td>
                                                        {file.bb_file_id
                                                            ? <span className={`${localStyles.badge} ${localStyles.badgeSuccess}`}>Yes</span>
                                                            : <span className={`${localStyles.badge} ${localStyles.badgeWarning}`}>No</span>
                                                        }
                                                    </td>
                                                    <td>
                                                        {!file.bb_file_id && (
                                                            <Button
                                                                id={`retry-photo-file-${file.id}`}
                                                                size="small"
                                                                background="bg-outline"
                                                                text="Retry"
                                                                click={() => handleRetry(file.id)}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className={localStyles.textMuted}>No files</p>
                            )}
                            <div className={localStyles.modalFooter}>
                                <Button
                                    id="photo-detail-close"
                                    size="small"
                                    background="bg-outline-grey"
                                    text="Close"
                                    click={() => setDetailId(null)}
                                />
                            </div>
                        </div>
                    }
                />
            )}

            <ConfirmDialog
                show={!!deleteTarget}
                title="Delete Photo Order"
                message={`Are you sure you want to delete photo order #${deleteTarget?.id}?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default <AdminPhotofixation/>;
